import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import { useSnackbar } from 'notistack';
import { TourDetailsFormResources, useTourDetailsFormQuery } from '~/queries/tours/useTourDetailsFormQueries';

import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Stack,
  Typography,
} from '@mui/material';

import { Request as APIRequest, Tour as ContractTour } from '@luxuryescapes/contract-svc-tour';

import { TOUR_TRAVELLER_REQUIREMENT_DOCUMENT, TOUR_TRAVELLER_REQUIREMENT_DOCUMENT_LABELS } from '../../constants';
import ExtendedRJSForm from '../RJSF/ExtendedRJSForm';

interface Props {
  tourId: string;
}

export default function TourTravellerRequirementsDetailsForm({ tourId }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const { fetchRequestInstance, fetch, patchRequestInstance, patch } = useTourDetailsFormQuery(
    TourDetailsFormResources.TRAVELLER_REQUIREMENTS,
  );

  // Controlled checkbox selection
  const [selectedDocs, setSelectedDocs] = useState<Array<ContractTour.RequirementTravelDocument>>([]);

  useEffect(() => {
    fetch(tourId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId]);

  // Derive initial selections from fetched form data
  const initialSelectedDocs = useMemo(() => {
    if (fetchRequestInstance.status !== 'succeeded' || !fetchRequestInstance.result) {
      return new Set<string>();
    }
    const incomingFormData: APIRequest.TravelRequirementsFormData = fetchRequestInstance.result.formData;
    const docReq = incomingFormData?.travellerRequirements?.documentsRequired;
    const namesArray: Array<ContractTour.RequirementTravelDocument> = Array.isArray(docReq) ? docReq : [];
    const filtered = namesArray.filter((name) =>
      (TOUR_TRAVELLER_REQUIREMENT_DOCUMENT as ReadonlyArray<string>).includes(name),
    );
    return new Set<string>(filtered);
  }, [fetchRequestInstance]);

  // Sync controlled selection when fetched data changes
  useEffect(() => {
    setSelectedDocs(Array.from(initialSelectedDocs) as Array<ContractTour.RequirementTravelDocument>);
  }, [initialSelectedDocs]);

  const handleRJSFSubmit = useCallback(
    ({ formData }: IChangeEvent<APIRequest.TravelRequirementsFormData>) => {
      const formDataUpdated: APIRequest.TravelRequirementsFormData = {
        travellerRequirements: {
          ...formData.travellerRequirements,
          documentsRequired: selectedDocs,
        },
      };
      patch(tourId, formDataUpdated);
    },
    [tourId, patch, selectedDocs],
  );

  useEffect(() => {
    switch (patchRequestInstance.status) {
      case 'succeeded':
        enqueueSnackbar('Traveller requirements updated successfully.', {
          variant: 'success',
        });
        break;
      case 'failed':
        enqueueSnackbar(`Submission failed! ${patchRequestInstance.error}`, {
          variant: 'error',
        });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchRequestInstance]);

  // Compute values used by render. Hooks must be called before any early returns.
  const disabled = patchRequestInstance.status === 'pending';
  const result = fetchRequestInstance.status === 'succeeded' ? fetchRequestInstance.result : null;
  const schema = result?.schema;
  const uiSchema = useMemo(() => {
    const base = result?.uiSchema;
    if (!base) {
      return undefined;
    }
    return {
      ...base,
      travellerRequirements: {
        ...(base?.travellerRequirements || {}),
        documentsRequired: {
          ...(base?.travellerRequirements?.documentsRequired || {}),
          'ui:widget': 'hidden',
        },
      },
    };
  }, [result]);
  const rjsfFormData = result?.formData;

  if (fetchRequestInstance.status === 'pending' || fetchRequestInstance.status === 'uninitiated') {
    return <CircularProgress />;
  }

  if (fetchRequestInstance.status === 'failed') {
    return (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            variant="outlined"
            onClick={() => {
              fetch(tourId);
            }}
          >
            Retry
          </Button>
        }
      >
        <AlertTitle>Failed to fetch the form.</AlertTitle>
        {fetchRequestInstance.error}
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <ExtendedRJSForm
        id="tour-traveller-requirements-details-form"
        schema={schema}
        uiSchema={uiSchema}
        formData={rjsfFormData}
        onSubmit={handleRJSFSubmit}
        busy={disabled}
        hideSubmitButton
      />
      <Stack spacing={1}>
        <Typography variant="h6">Document types</Typography>
        <FormControl component="fieldset" variant="standard">
          <FormLabel>Select required document types</FormLabel>
          <FormGroup>
            {TOUR_TRAVELLER_REQUIREMENT_DOCUMENT.map((doc) => (
              <FormControlLabel
                key={doc}
                control={
                  <Checkbox
                    checked={selectedDocs.includes(doc as ContractTour.RequirementTravelDocument)}
                    onChange={(e) => {
                      const { checked } = e.target;
                      setSelectedDocs((prev) =>
                        checked
                          ? prev.includes(doc as ContractTour.RequirementTravelDocument)
                            ? prev
                            : prev.concat([doc as ContractTour.RequirementTravelDocument])
                          : prev.filter((d) => d !== (doc as ContractTour.RequirementTravelDocument)),
                      );
                    }}
                    value={doc}
                    name={doc}
                  />
                }
                label={<span>{TOUR_TRAVELLER_REQUIREMENT_DOCUMENT_LABELS[doc]}</span>}
              />
            ))}
          </FormGroup>
        </FormControl>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          color="primary"
          type="submit"
          form="tour-traveller-requirements-details-form"
          disabled={disabled}
        >
          Save
        </Button>
      </Stack>

      {patchRequestInstance.status === 'failed' && <Alert severity="error">{patchRequestInstance.error}</Alert>}
    </Stack>
  );
}
